import { get, ref } from "firebase/database";

export const normalizeSocialAccount = (account = {}, defaultPlatform = "facebook") => {
  const platform = (account.platform || defaultPlatform || "facebook").toLowerCase();
  const accountId =
    account.accountId ||
    account.id ||
    account.oaId ||
    account.oaid ||
    account.fanpageId ||
    account.fanpageID ||
    "";

  return {
    ...account,
    platform,
    accountId: String(accountId),
    id: String(accountId),
    name:
      account.name ||
      account.accountName ||
      account.fanpageName ||
      account.oaName ||
      `${platform} ${accountId}`,
  };
};

export const getSocialAccountKey = (account = {}) => {
  const normalized = normalizeSocialAccount(account, account.platform || "facebook");
  return `${normalized.platform}:${normalized.accountId}`;
};

export const getProductAccounts = (product = {}) => {
  if (
    Array.isArray(product?.selectedAccounts) &&
    product.selectedAccounts.length > 0
  ) {
    return product.selectedAccounts
      .map((account) =>
        normalizeSocialAccount(account, account.platform || product.platform || "facebook")
      )
      .filter((account) => account.accountId);
  }

  if (Array.isArray(product?.fanpages) && product.fanpages.length > 0) {
    return product.fanpages
      .map((account) => normalizeSocialAccount(account, "facebook"))
      .filter((account) => account.accountId);
  }

  return [];
};

export const getAccountPosition = (product = {}, account = {}) => {
  const normalized = normalizeSocialAccount(account, account.platform || "facebook");
  const platformKey = getSocialAccountKey(normalized);
  const legacyKey = normalized.accountId;
  const positions =
    product?.fanpagePositions && typeof product.fanpagePositions === "object"
      ? product.fanpagePositions
      : {};

  return (
    positions[platformKey] ??
    positions[legacyKey] ??
    product?.position ??
    Number.MAX_SAFE_INTEGER
  );
};

export const loadConnectedSocialAccounts = async (db, userId, options = {}) => {
  if (!userId) return [];

  const platformFilter =
    typeof options?.platform === "string"
      ? options.platform.trim().toLowerCase()
      : "";

  if (platformFilter === "zalo") {
    const channelsSnapshot = await get(
      ref(db, `users/${userId}/connectedChannels/zalo`)
    );
    const zaloAccounts = channelsSnapshot.exists() ? channelsSnapshot.val() : {};
    return Object.values(zaloAccounts || {})
      .map((account) => normalizeSocialAccount(account, "zalo"))
      .filter((account) => account.accountId);
  }

  if (platformFilter === "facebook") {
    const [channelsSnapshot, fanpagesSnapshot] = await Promise.all([
      get(ref(db, `users/${userId}/connectedChannels/facebook`)),
      get(ref(db, `users/${userId}/connectedFanpages`)),
    ]);

    const accountsByKey = new Map();
    const channels = channelsSnapshot.exists() ? channelsSnapshot.val() : {};
    Object.values(channels || {}).forEach((account) => {
      const normalized = normalizeSocialAccount(account, "facebook");
      if (normalized.accountId) {
        accountsByKey.set(getSocialAccountKey(normalized), normalized);
      }
    });

    const legacyFanpages = fanpagesSnapshot.exists() ? fanpagesSnapshot.val() : {};
    Object.values(legacyFanpages || {}).forEach((fanpage) => {
      const normalized = normalizeSocialAccount(fanpage, "facebook");
      if (normalized.accountId) {
        accountsByKey.set(getSocialAccountKey(normalized), normalized);
      }
    });

    return Array.from(accountsByKey.values());
  }

  const [channelsSnapshot, fanpagesSnapshot] = await Promise.all([
    get(ref(db, `users/${userId}/connectedChannels`)),
    get(ref(db, `users/${userId}/connectedFanpages`)),
  ]);

  const accountsByKey = new Map();
  const channels = channelsSnapshot.exists() ? channelsSnapshot.val() : {};

  Object.entries(channels || {}).forEach(([platform, accounts]) => {
    Object.values(accounts || {}).forEach((account) => {
      const normalized = normalizeSocialAccount(account, platform);
      if (normalized.accountId) {
        accountsByKey.set(getSocialAccountKey(normalized), normalized);
      }
    });
  });

  const legacyFanpages = fanpagesSnapshot.exists() ? fanpagesSnapshot.val() : {};
  Object.values(legacyFanpages || {}).forEach((fanpage) => {
    const normalized = normalizeSocialAccount(fanpage, "facebook");
    if (normalized.accountId) {
      accountsByKey.set(getSocialAccountKey(normalized), normalized);
    }
  });

  return Array.from(accountsByKey.values());
};

export const toSelectedAccountPayload = (account = {}) => {
  const normalized = normalizeSocialAccount(account, account.platform || "facebook");
  return {
    platform: normalized.platform,
    accountId: normalized.accountId,
    id: normalized.accountId,
    name: normalized.name,
  };
};

export const toLegacyFacebookFanpagePayload = (account = {}) => {
  const normalized = normalizeSocialAccount(account, "facebook");
  return {
    id: normalized.accountId,
    name: normalized.name,
    access_token: normalized.access_token || normalized.accessToken || "",
  };
};

export const buildFacebookConnectedChannelMap = (fanpages = {}) => {
  const entries = Array.isArray(fanpages)
    ? fanpages.map((fanpage) => [fanpage.id || fanpage.accountId, fanpage])
    : Object.entries(fanpages || {});
  const now = Date.now();

  return entries.reduce((accumulator, [fanpageId, fanpage]) => {
    if (!fanpageId || !fanpage) return accumulator;

    accumulator[fanpageId] = {
      platform: "facebook",
      accountId: String(fanpageId),
      id: String(fanpageId),
      name: fanpage.name || fanpage.fanpageName || `Facebook ${fanpageId}`,
      avatar: fanpage.avatar || fanpage.picture || "",
      cover: fanpage.cover || "",
      tokenStatus: fanpage.access_token ? "active" : fanpage.tokenStatus || "missing",
      tokenExpiresAt: fanpage.tokenExpiry || fanpage.tokenExpiresAt || null,
      permissions: fanpage.permissions || fanpage.grantedScopes || [],
      connectedAt: fanpage.connectedAt || now,
      lastRefreshed: fanpage.lastRefreshed || now,
    };
    return accumulator;
  }, {});
};

export const hasZaloAccount = (accounts = []) =>
  accounts.some((account) => (account.platform || "facebook") === "zalo");
